import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ENDPOINT, IMAGE_BASE_URL } from "../../../endpoints/endpoints";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const TeamDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState(false);

  // Token check
  useEffect(() => {
    if (!localStorage.getItem("accessToken")) {
      toast.warning("Please log in to continue");
      navigate("/login");
    }
  }, [navigate]);

  // Fetch Team Detail
  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("accessToken");

        const res = await axios.get(ENDPOINT.TEAM_DETAIL(id), {
          headers: { Authorization: `Bearer ${token}` },
        });

        // 🔥 FIXED: your backend uses "result" NOT "data"
        setTeam(res.data.result || res.data.data);
      } catch (err) {
        toast.error("Failed to fetch team detail");
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [id]);

  // Delete handler
  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("accessToken");

      const res = await axios.delete(ENDPOINT.TEAM_DELETE(id), {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Team deleted successfully!");

      setTimeout(() => navigate("/team/list"), 1200);
    } catch (err) {
      toast.error("Failed to delete team");
    } finally {
      setDeleteDialog(false);
    }
  };

  // Loading
  if (loading) {
    return (
      <Box display="flex" height="80vh" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  // Not found
  if (!team) {
    return (
      <Container maxWidth="md" sx={{ mt: 5 }}>
        <Typography variant="h6" align="center" color="text.secondary">
          Team not found.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 5, mb: 5 }}>
      <ToastContainer />

      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight={600}>
            🖼 Team Detail
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/team/list")}
            sx={{ textTransform: "none" }}
          >
            ← Back to List
          </Button>
        </Stack>

        <Divider sx={{ mb: 3 }} />

        {/* Image */}
        <Box textAlign="center" mb={3}>
          <Box
            component="img"
            src={`${IMAGE_BASE_URL}${team.image}`} // 🔥 FIXED
            alt="team"
            sx={{
              width: "100%",
              maxWidth: 150,
              borderRadius: 2,
              border: "1px solid #ddd",
              objectFit: "cover",
              boxShadow: 2,
            }}
          />
        </Box>

        {/* Details */}
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Name
            </Typography>
            <Typography variant="body1">{team.name}</Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              League
            </Typography>
            <Typography variant="body1"><Box
                                  component="img"
                                  src={`${IMAGE_BASE_URL}${team.league.image}`} 
                                  alt={team.league.name}
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                    mr: 1.5,
                                  }}
                                />{team.league.name}</Typography>
          </Box>
        </Stack>

        <Divider sx={{ my: 3 }} />

        {/* Action Buttons */}
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button
            variant="outlined"
            color="warning"
            onClick={() => navigate(`/team/update/${team.id}`)}
            sx={{ textTransform: "none" }}
          >
            Edit Team
          </Button>

          <Button
            variant="contained"
            color="error"
            sx={{ textTransform: "none" }}
            onClick={() => setDeleteDialog(true)}
          >
            Delete
          </Button>
        </Stack>
      </Paper>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this team? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TeamDetail;
